import { all, call, cancel, fork, put, select, take } from 'typed-redux-saga/macro';

import config, { getRoute, isEnabled } from 'Config';
import { getInstance as getConfigServiceInstance } from 'Lib/ConfigService';
import { LEVER_GLOBAL_PASSPORT_ENABLED, LEVER_WEB_PAYMENT_ENABLED } from 'Lib/ConfigService/levers';
import {
  I13N_ACTION_CHANGE_VALUE,
  I13N_CATEGORY_APP,
  I13N_CATEGORY_SETTINGS,
  I13N_TYPE_FEATURE_COUNT,
  I13N_TYPE_SET_VALUE,
  I13N_TYPE_VIEW,
} from 'Lib/constants/i13nSpec';
import { debugUser as debug } from 'Lib/debug';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { flush, getRehydrateLifecycle } from 'Lib/persist';
import { GetActionByType } from 'Lib/reduxType';
import { SELFIE_CHALLENGE_REDIRECT, withErrorHandling } from 'Lib/sagaHelpers';
import { localStorage as storage } from 'Lib/storage';
import { getServiceFn } from 'Services';
import { PROFILE_SERVICE, USER_SERVICE } from 'Services/constants';
import {
  PROFILE_LIKES,
  PROFILE_OFFERINGS,
  PROFILE_PAYWALLS,
  PROFILE_PLUS_CONTROL,
  PROFILE_PURCHASE,
  PROFILE_USER,
} from 'Services/ProfileService/constants';

import { DEACTIVATE_TAB } from 'Store/app/constants';
import {
  toggleConsumables,
  toggleGold,
  togglePlatinum,
  toggleTinderPlus,
} from 'Store/appSettings/actions';
import {
  getAgeVerificationIsSessionOnboardingSkippable,
  getAgeVerificationShowSessionOnboardingModal,
  getIsConsumablesEnabled,
  getIsEmailVerificationDismissible,
  getIsEmailVerificationRequired,
  getIsGoldEnabled,
  getIsPlatinumEnabled,
  getIsTermsAcceptanceNeeded,
  getIsTinderPlusEnabled,
  getShouldShowTermsUpdate,
  getTermsVersion,
} from 'Store/appSettings/selectors';
import {
  ASYNC_FEATURE_ACCOUNT,
  ASYNC_FEATURE_BOOST,
  ASYNC_FEATURE_CONSENTS,
  ASYNC_FEATURE_CONTACTS,
  ASYNC_FEATURE_EXPLORE,
  ASYNC_FEATURE_FILTERS,
  ASYNC_FEATURE_INSENDIO,
  ASYNC_FEATURE_LIKES_YOU,
  ASYNC_FEATURE_MEDIA,
  ASYNC_FEATURE_MUTUAL_FRIENDS,
  ASYNC_FEATURE_MY_LIKES,
  ASYNC_FEATURE_PASSPORT,
  ASYNC_FEATURE_PERFORMANCE_METRICS,
  ASYNC_FEATURE_PROFILE,
  ASYNC_FEATURE_RECS,
  ASYNC_FEATURE_REPORT,
  ASYNC_FEATURE_SEND_MESSAGE_AND_MATCH,
  ASYNC_FEATURE_SETTINGS,
  ASYNC_FEATURE_SPOTIFY,
  ASYNC_FEATURE_STACKS_UTIL,
  ASYNC_FEATURE_UPDATES,
  ASYNC_FEATURE_USER_PHOTO,
  ASYNC_FEATURE_WEBSOCKET,
  loadAsyncBundle,
} from 'Store/asyncSaga';
import { uploadQueuedPhotosSaga } from 'Store/authOnboarding/logic';
import { startPollPrimetimeBoost } from 'Store/boost/actions';
import { LOGOUT } from 'Store/commonActions';
import { initConsents } from 'Store/consents/actions';
import { STORE_LIKES_YOU } from 'Store/constants';
import { getMD, getPaResponse, getPid } from 'Store/context/selectors';
import { cancelEmailVerification, verifyEmailOtp } from 'Store/email/actions';
import { TRIGGER_EMAIL_COLLECTION_PAYLOAD_NAME } from 'Store/email/tracking';
import {
  PRODUCT_TYPE_GOLD,
  PRODUCT_TYPE_PLATINUM,
  PRODUCT_TYPE_PLUS,
} from 'Store/entities/products.schema';
import {
  setNewLikesCount,
  setPrevSessionLikesCount,
  showNewLikesCelebrationModal,
  throttledFetchLikesYouPreview,
} from 'Store/likesYou/actions';
import { FETCH_LIKES_YOU_PREVIEW_SUCCESS } from 'Store/likesYou/constants';
import { getLikesYouCount } from 'Store/likesYou/selectors';
import { locationInitSaga } from 'Store/location/logic';
import { fetchMeta } from 'Store/meta/actions';
import { FETCH_META_ERROR, FETCH_META_SUCCESS } from 'Store/meta/constants';
import { authLogout, setAppealToken, setSelfieChallengeToken } from 'Store/mfa/actions';
import { LOGOUT_SUCCESS } from 'Store/mfa/constants';
import { getMagicLinkType } from 'Store/mfa/selectors';
import { openModal } from 'Store/modals/actions';
import {
  MODAL_AGE_VERIFICATION,
  MODAL_EMAIL_VERIFICATION,
  MODAL_TERMS_OF_SERVICE,
  MODAL_WARNING,
} from 'Store/modals/constants';
import {
  notificationsCheck,
  notificationsUnsubscribe,
  resetNotifications,
  showNotification,
} from 'Store/notifications/actions';
import { getIsOnboardingOpen } from 'Store/onboarding/selectors';
import { fetchProfile } from 'Store/profile/actions';
import { FETCH_PROFILE_ERROR, FETCH_PROFILE_SUCCESS } from 'Store/profile/constants';
import { setHelpFormCookies } from 'Store/profile/deeplink.logic';
import {
  getHasBoostProducts,
  getHasGoldProducts,
  getHasGoldSubscription,
  getHasPlatinumProducts,
  getHasPlatinumSubscription,
  getHasPlusProducts,
  getHasPlusSubscription,
  getHasPremiumSubscription,
  getHasSubscription,
  getHasSuperLikeProducts,
} from 'Store/profile/revenue.selectors';
import {
  getProfileNotifications,
  getProfileUserId,
  getShowFastMatchIntro,
  selectUserPrompts,
} from 'Store/profile/selectors';
import { getIsSelfieChallengeRequired } from 'Store/profile/selfieVerification.selectors';
import { purchase3D } from 'Store/purchase/actions';
import { getHasSavedCC, getHasSavedPayPal } from 'Store/purchase/selectors';
import { cancelRecsChannel, loadRecById, loadRecsChannel } from 'Store/recs/actions';
import { LOAD_REC_BY_ID_ERROR, LOAD_REC_BY_ID_SUCCESS } from 'Store/recs/constants';
import { navigate } from 'Store/routing/actions';
import { SELFIE_CHALLENGE_SUCCESS } from 'Store/selfieChallenge/constants';
import { verifyTinderUStatus } from 'Store/tinderU/actions';
import { cancelUpdatesRootSaga, startUpdatesRootSaga } from 'Store/updates/actions';
import {
  deleteAccountSuccess,
  requestAgeVerificationUrlError,
  requestAgeVerificationUrlSuccess,
  verifyAgeVerificationStatus,
  verifyLoginRedirect,
} from 'Store/user/actions';
import {
  COMPLETE_3DS,
  DEEPLINK_TYPE_ACCOUNT_EMAIL,
  DEEPLINK_TYPE_AGE_VERIFICATION,
  DEEPLINK_TYPE_LOGIN_REDIRECT,
  DEEPLINK_TYPE_TINDER_U,
  DELETE_ACCOUNT,
} from 'Store/user/constants';
import {
  getAgeVerificationUrl,
  getDeepLinkNavigationRoute,
  getDeepLinkOtp,
  getDeepLinkType,
  getPostLoginRedirectPath,
  getPostLoginRedirectPushState,
} from 'Store/user/selectors';
import { getUserID as getWebProfileUserID } from 'Store/webProfile/selectors';
import { websocketInit } from 'Store/websocket/actions';

import { trackEvent } from 'Store/sparks/actions';
import {
  DYNAMIC_UI_EDIT,
  Type as DynamicUIEditType,
} from 'Store/sparks/dynamicUI/events/dynamicUIEdit';
import {
  LOGOUT_START as EVENT_LOGOUT_START,
  MEMBERSHIP_INTERACT,
  TRIGGER_RECEIVE,
} from 'Store/sparks/sparks.events';
import {
  PROPERTY_SELECT_SUBSCRIPTION,
  TYPE_MEMBERSHIP_MANAGEMENT,
} from 'Store/sparks/tinderSelect/events';

import { getFormattedPrompts } from 'Containers/PromptsTextEditor/hooks/useAnalytics';
import {
  NOTIFICATION_THEME_ERROR,
  NOTIFICATION_THEME_SUCCESS,
} from 'Components/InAppNotification/constants';

import handleModalsPostLogin from './utils/handleModalsPostLogin';

const { executeEvent } = getI13nUtils();

const VERIFICATION_TYPES = {
  [DEEPLINK_TYPE_ACCOUNT_EMAIL]: verifyEmailOtp,
  [DEEPLINK_TYPE_TINDER_U]: verifyTinderUStatus,
  [DEEPLINK_TYPE_LOGIN_REDIRECT]: verifyLoginRedirect,
  [DEEPLINK_TYPE_AGE_VERIFICATION]: verifyAgeVerificationStatus,
};

/**
 * set user id, subscription, saved payment methods in analytics
 */
let storedUserId, storedUserSub, storedSavedPaymentMethods;

// https://github.com/TinderApp/TinderAndroid/blob/main/common/hash-utils/src/main/kotlin/com/tinder/common/hash/utils/HashUtils.kt#L41
// https://github.com/TinderApp/tinder_ios/blob/798b3cedb7fccdfe0a6adfbfac06e08684c2b7f3/Projects/Modules/Utility/Matchbox/Sources/Matchbox/Foundation/NSString%2BTinder.swift#L111-L127
const getSaltedHashId = (uid: string, hashedFunction) => {
  if (!uid || uid.length < 8) {
    return null;
  }
  const mid = uid.length / 2 - 1;
  const salt = `${uid.substring(Math.max(0, uid.length - 5))}613${uid.substring(
    0,
    2
  )}125${uid.substring(mid - 3, mid)}1125${uid.substring(mid, mid + 4)}`;

  return hashedFunction(uid + salt);
};

export function* setUserIdForAnalytics() {
  const { default: sha1 } = yield import('crypto-js/sha1' /* webpackChunkName: "crypto-js" */);
  const userId = yield* select(getProfileUserId);

  // only set user id per session
  if (userId && storedUserId !== userId) {
    const hashedID = getSaltedHashId(userId, sha1).toString();
    storedUserId = userId;
    executeEvent(I13N_TYPE_SET_VALUE, {
      key: 'user_id',
      value: hashedID,
    });
  }
}

export function* setUserSubscriptionForAnalytics() {
  const hasPlusSub = yield* select(getHasPlusSubscription);
  const hasGoldSub = yield* select(getHasGoldSubscription);
  const hasPlatinumSub = yield* select(getHasPlatinumSubscription);

  let userSub = 'free';
  if (hasPlusSub) {
    userSub = PRODUCT_TYPE_PLUS;
  }
  if (hasGoldSub) {
    userSub = PRODUCT_TYPE_GOLD;
  }
  if (hasPlatinumSub) {
    userSub = PRODUCT_TYPE_PLATINUM;
  }
  if (userSub && storedUserSub !== userSub) {
    storedUserSub = userSub;
    executeEvent(I13N_TYPE_SET_VALUE, {
      key: 'user_sub',
      value: userSub,
    });
  }
}

export function* setUserPaymentTypeForAnalytics() {
  const savedCC = (yield* select(getHasSavedCC)) ? 'cc' : undefined;
  const savedPayPal = (yield* select(getHasSavedPayPal)) ? 'paypal' : undefined;
  const savedPaymentMethods = [savedCC, savedPayPal];

  if (savedPaymentMethods.length && storedSavedPaymentMethods !== savedPaymentMethods) {
    storedSavedPaymentMethods = savedPaymentMethods;
    executeEvent(I13N_TYPE_SET_VALUE, {
      key: 'user_saved_payment_methods',
      value: savedPaymentMethods.toString(),
    });
  }
}

export function* setUserDataForAnalytics() {
  yield* setUserIdForAnalytics();
  yield* setUserSubscriptionForAnalytics();
  yield* setUserPaymentTypeForAnalytics();
}

/**
 * Sagas
 */
function* handleDeeplinkVerification() {
  // verify deeplink otp
  const isVerificationCodePresent = yield* select(getDeepLinkOtp);
  const verificationType = yield* select(getDeepLinkType);
  const verificationFunction = verificationType ? VERIFICATION_TYPES[verificationType] : null;
  if (isVerificationCodePresent && verificationFunction) {
    yield* put(verificationFunction());
  }
}

function* handleWebProfile() {
  const webProfileID = yield* select(getWebProfileUserID);
  const currentUserID = yield* select(getProfileUserId);
  if (webProfileID && webProfileID !== currentUserID) {
    yield* put(loadRecById({ id: webProfileID }));
    yield* take([LOAD_REC_BY_ID_SUCCESS, LOAD_REC_BY_ID_ERROR]);
  }
}

/**
 * Post login saga
 * Steps:
 * 1. get user profile (non-blocking)
 * 2. get location
 * 3. post meta for updating location
 * 4. get updated user profile and get recs
 */
let postLoginFlag = false;
export function* _postLoginSaga() {
  // There seems to be a case where this gets triggered twice and floods the updates endpoint.
  // We don't know exactly why, so this flag is a kludgy way of managing the bad side-effects.
  if (postLoginFlag) {
    debug('postLoginSaga is already active!');
    return;
  }

  postLoginFlag = true;

  // When onboarding is open, make a blocking call to get meta groups (without position info) for A/B testing
  const isOnboardingOpen = yield* select(getIsOnboardingOpen);
  if (isOnboardingOpen) {
    yield* put(fetchMeta(true));
    yield* take([FETCH_META_SUCCESS, FETCH_META_ERROR]);
  }

  // main profile features
  yield* all([
    call(loadAsyncBundle, ASYNC_FEATURE_ACCOUNT),
    call(loadAsyncBundle, ASYNC_FEATURE_MEDIA),
    call(loadAsyncBundle, ASYNC_FEATURE_PROFILE),
    call(loadAsyncBundle, ASYNC_FEATURE_SETTINGS),
    call(loadAsyncBundle, ASYNC_FEATURE_UPDATES),
    call(loadAsyncBundle, ASYNC_FEATURE_SEND_MESSAGE_AND_MATCH),
    call(loadAsyncBundle, ASYNC_FEATURE_USER_PHOTO),
    call(loadAsyncBundle, ASYNC_FEATURE_INSENDIO),
  ]);

  // rest of the features, don't wait for location returns
  // doesn't need to block profile call
  yield* fork(function* loadRestFeatures() {
    yield* all([
      call(loadAsyncBundle, ASYNC_FEATURE_BOOST),
      call(loadAsyncBundle, ASYNC_FEATURE_CONTACTS),
      call(loadAsyncBundle, ASYNC_FEATURE_EXPLORE),
      call(loadAsyncBundle, ASYNC_FEATURE_FILTERS),
      call(loadAsyncBundle, ASYNC_FEATURE_LIKES_YOU),
      call(loadAsyncBundle, ASYNC_FEATURE_MUTUAL_FRIENDS),
      call(loadAsyncBundle, ASYNC_FEATURE_MY_LIKES),
      call(loadAsyncBundle, ASYNC_FEATURE_PASSPORT),
      call(loadAsyncBundle, ASYNC_FEATURE_PERFORMANCE_METRICS),
      call(loadAsyncBundle, ASYNC_FEATURE_REPORT),
      call(loadAsyncBundle, ASYNC_FEATURE_SPOTIFY),
    ]);
    if (isEnabled('stacksUtil')) {
      yield* call(loadAsyncBundle, ASYNC_FEATURE_STACKS_UTIL);
    }
  });

  const magicLinkType = yield* select(getMagicLinkType);
  if (magicLinkType) {
    yield* put(
      trackEvent(MEMBERSHIP_INTERACT, {
        property: PROPERTY_SELECT_SUBSCRIPTION,
        type: TYPE_MEMBERSHIP_MANAGEMENT,
        subtype: 'authenticate',
        action: 'authenticate',
      })
    );
  }

  const postLoginRedirectPath = yield* select(getPostLoginRedirectPath);
  if (postLoginRedirectPath) {
    const postLoginRedirectPushState = yield* select(getPostLoginRedirectPushState);

    if (!postLoginRedirectPushState) {
      // Do a full page refresh
      window.location.href = postLoginRedirectPath;
      return;
    }

    // otherwise just push state navigation
    yield* put(navigate(postLoginRedirectPath));
  }

  const deepLinkType = yield* select(getDeepLinkType);
  const isSelectDeeplink = deepLinkType === 'select';
  if (isEnabled('skipPostLogin') || isSelectDeeplink) {
    return;
  }

  // Get minimal user data right after login
  yield* put(
    fetchProfile({
      inputFields: [
        PROFILE_LIKES,
        PROFILE_OFFERINGS,
        // NOTE: Add paywall query parameter to solve the legacy paywall showed up prior to the dyno paywall issue
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        PROFILE_PAYWALLS,
        PROFILE_PLUS_CONTROL,
        PROFILE_PURCHASE,
        PROFILE_USER,
      ].filter(Boolean),
    })
  );
  yield* take([FETCH_PROFILE_SUCCESS, FETCH_PROFILE_ERROR]);

  // Get user prompts for analytics after fetched profile
  const { prompts } = (yield* select(selectUserPrompts)) ?? {};
  yield* put(
    trackEvent(DYNAMIC_UI_EDIT, {
      type: DynamicUIEditType.USER_PROMPTS,
      value: getFormattedPrompts(prompts),
    })
  );

  yield* put(startUpdatesRootSaga());

  // TODO: Replace with the in-app notification insendio integration
  yield* put(startPollPrimetimeBoost());

  // Wait for location before we call meta with position information
  const location = yield* call(locationInitSaga);
  yield* put(fetchMeta(true));
  yield* take([FETCH_META_SUCCESS, FETCH_META_ERROR]);

  // show terms modal if enabled and acceptance is needed
  const isTermsEnabled = yield* select(getShouldShowTermsUpdate);
  const isTermsAcceptanceNeeded = yield* select(getIsTermsAcceptanceNeeded);
  const showTermsUpdateModal = isTermsEnabled && isTermsAcceptanceNeeded;
  if (!__TEST__ && showTermsUpdateModal) {
    yield* put(
      openModal(MODAL_TERMS_OF_SERVICE, {
        props: {
          forceOpen: true,
        },
      })
    );
  }

  yield* call(handleModalsPostLogin);

  // Redirect users requiring selfie challenge when enabled
  const isSelfieChallenged = yield* select(getIsSelfieChallengeRequired);

  if (isSelfieChallenged) {
    yield* put(navigate(SELFIE_CHALLENGE_REDIRECT, { replace: true }));
    take(SELFIE_CHALLENGE_SUCCESS);
  }

  // get user data after meta update
  yield* put(fetchProfile());
  yield* take([FETCH_PROFILE_SUCCESS, FETCH_PROFILE_ERROR]);

  yield* fork(uploadQueuedPhotosSaga);

  // Wait for profile response before handling profile notifications
  yield* call(handleNotifications);

  // verify deeplink otp
  yield* call(handleDeeplinkVerification);

  const pid = yield* select(getPid);
  if (pid) {
    const mD = yield* select(getMD);
    const paRes = yield* select(getPaResponse);
    const purchasePayload = {
      id: pid,
      md: mD,
      paResponse: paRes,
    };
    yield* put(purchase3D(purchasePayload));
    yield* take(COMPLETE_3DS);
    yield* put(navigate(getRoute('checkout')));
  }

  const shouldShowAgeVerificationModal = yield* select(
    getAgeVerificationShowSessionOnboardingModal
  );
  if (shouldShowAgeVerificationModal) {
    const skippable = yield* select(getAgeVerificationIsSessionOnboardingSkippable);
    yield* put(
      openModal(MODAL_AGE_VERIFICATION, {
        props: {
          skippable,
          forceOpen: !skippable,
        },
      })
    );
  }

  const isEmailVerificationRequired = yield* select(getIsEmailVerificationRequired);
  const isEmailVerificationDismissible = yield* select(getIsEmailVerificationDismissible);
  const isVerificationCodePresent = yield* select(getDeepLinkOtp);
  if (isEmailVerificationRequired && !isVerificationCodePresent) {
    yield* put(
      openModal(MODAL_EMAIL_VERIFICATION, {
        props: {
          forceOpen: !isEmailVerificationDismissible,
          handleDismissModal: cancelEmailVerification(),
        },
      })
    );
  }
  yield* put(
    trackEvent(TRIGGER_RECEIVE, {
      emailPromptDismissible: isEmailVerificationDismissible,
      emailPromptRequired: isEmailVerificationRequired,
      payloadName: TRIGGER_EMAIL_COLLECTION_PAYLOAD_NAME,
      type: 'meta',
    })
  );

  yield* call(loadAsyncBundle, ASYNC_FEATURE_CONSENTS);
  yield* put(initConsents());

  yield* call(loadAsyncBundle, ASYNC_FEATURE_RECS);
  yield* call(handleWebProfile);
  yield* put(loadRecsChannel());

  // Ensure that we have permission for notifications in general
  // Skip check when onboarding is open to avoid browser notification prompt showing up before the onboarding prompt
  if (!isOnboardingOpen) {
    yield* put(notificationsCheck());
  }

  const togglePaidFeatureFlagsTask = yield* fork(togglePaidFeatureFlags);
  // Clear notification service queue
  yield* put(resetNotifications());

  // navigate to new page from a deeplink
  const deeplinkNavigationRoute = yield* select(getDeepLinkNavigationRoute);
  const configService = getConfigServiceInstance();
  const isGlobalPassportEnabled = configService.readLever(LEVER_GLOBAL_PASSPORT_ENABLED);
  if (deeplinkNavigationRoute) {
    if (
      (isGlobalPassportEnabled && deeplinkNavigationRoute === 'passport') ||
      !isGlobalPassportEnabled
    ) {
      yield* put(navigate(getRoute(deeplinkNavigationRoute)));
    }
  }

  yield* take([LOGOUT, LOGOUT_SUCCESS, DELETE_ACCOUNT, DEACTIVATE_TAB]);
  yield* put(cancelRecsChannel());
  yield* put(cancelUpdatesRootSaga());

  // @ts-expect-error - multiple sub tasks in the cancelation
  yield* cancel(location);
  yield* cancel(togglePaidFeatureFlagsTask);
  postLoginFlag = false;
}

export const postLoginSaga = withErrorHandling(_postLoginSaga, {
  name: 'postLoginSaga',
  errorHandler() {
    postLoginFlag = false;
  },
});

function* _postGuestLoginSaga() {
  if (isEnabled('websocketActive')) {
    yield* call(loadAsyncBundle, ASYNC_FEATURE_WEBSOCKET);
    yield* put(websocketInit());
  }
  yield* all([
    call(loadAsyncBundle, ASYNC_FEATURE_RECS),
    call(loadAsyncBundle, ASYNC_FEATURE_PROFILE),
  ]);

  yield* put(fetchMeta(true));
}

export const postGuestLoginSaga = withErrorHandling(_postGuestLoginSaga);

function* togglePaidFeatureFlags() {
  const configService = getConfigServiceInstance();
  const isWebPaymentEnabled = configService.readLever(LEVER_WEB_PAYMENT_ENABLED);

  const hasPlusProduct = yield* select(getHasPlusProducts);
  const hasSubscription = yield* select(getHasSubscription);
  const isTPlusEnabled = yield* select(getIsTinderPlusEnabled);
  const shouldEnableTPlus = hasSubscription || (isWebPaymentEnabled && hasPlusProduct);

  if (isTPlusEnabled !== shouldEnableTPlus) {
    yield* put(toggleTinderPlus(shouldEnableTPlus));
    sendMetaBeacon(shouldEnableTPlus, 'TinderPlus');
  }

  const hasBoostProducts = yield* select(getHasBoostProducts);
  const hasSuperlikeProducts = yield* select(getHasSuperLikeProducts);
  const isConsumablesEnabled = yield* select(getIsConsumablesEnabled);
  const shouldEnableConsumables = isWebPaymentEnabled && hasSuperlikeProducts && hasBoostProducts;

  if (isConsumablesEnabled !== shouldEnableConsumables) {
    yield* put(toggleConsumables(shouldEnableConsumables));
    sendMetaBeacon(shouldEnableConsumables, 'Consumables');
  }

  const hasGoldSubscription = yield* select(getHasGoldSubscription);
  const hasGoldProducts = yield* select(getHasGoldProducts);
  const isGoldEnabled = yield* select(getIsGoldEnabled);

  const shouldEnableGold = hasGoldSubscription || (isWebPaymentEnabled && hasGoldProducts);

  if (isGoldEnabled !== shouldEnableGold) {
    yield* put(toggleGold(shouldEnableGold));
    sendMetaBeacon(shouldEnableGold, 'TinderGold');
  }

  const isPlatinumEnabled = yield* select(getIsPlatinumEnabled);
  const hasPlatinumProducts = yield* select(getHasPlatinumProducts);
  const hasPlatinumSubscription = yield* select(getHasPlatinumSubscription);
  const shouldEnablePlatinum =
    hasPlatinumSubscription || (isWebPaymentEnabled && hasPlatinumProducts);

  if (isPlatinumEnabled !== shouldEnablePlatinum) {
    yield* put(togglePlatinum(shouldEnablePlatinum));
    sendMetaBeacon(shouldEnablePlatinum, 'TinderPlatinum');
  }

  if (shouldEnableGold || shouldEnablePlatinum) {
    yield* put(throttledFetchLikesYouPreview());
    // Celebration likes modal logic
    // wait for likes you count and persist data to load
    yield* all([
      take(FETCH_LIKES_YOU_PREVIEW_SUCCESS),
      call(getRehydrateLifecycle(STORE_LIKES_YOU).block),
    ]);
    yield* put(setNewLikesCount());
    const showFastMatchIntro = yield* select(getShowFastMatchIntro);
    const hasPremiumSubscription = yield* select(getHasPremiumSubscription);
    if (shouldEnableGold && !hasPremiumSubscription && !showFastMatchIntro) {
      yield* put(showNewLikesCelebrationModal());
    }
  }
}

export function* logoutSaga(action: GetActionByType<typeof LOGOUT>) {
  postLoginFlag = false;
  if (action.payload.userTriggered) {
    yield* put(trackEvent(EVENT_LOGOUT_START, { method: 'button' }));
  }

  // set current likes in redux persist store
  const likesYouCount = yield* select(getLikesYouCount);
  yield* put(setPrevSessionLikesCount(likesYouCount));

  // Remove Emoji picker history
  storage.removeItem('emojiPicker.recent');

  // clear appeal token
  yield* put(setAppealToken(''));

  // clear selfie challenge token
  yield* put(setSelfieChallengeToken(''));

  yield* put(notificationsUnsubscribe());

  yield* put(authLogout());
}

export function* _deleteAccountSaga(action: GetActionByType<typeof DELETE_ACCOUNT>) {
  const { deletionModalVersion } = action.payload;
  yield* put(notificationsUnsubscribe());
  const result = yield* call(
    yield* call(getServiceFn, PROFILE_SERVICE, 'deleteAccount' as const),
    deletionModalVersion
  );

  yield* put(authLogout());

  debug('_deleteAccountSaga', result);

  yield* put(deleteAccountSuccess());

  debug('_deleteAccountSaga success');
}

export const deleteAccountSaga = withErrorHandling(
  _deleteAccountSaga,
  {
    name: 'deleteAccountSaga',
    *finalHandler() {
      yield* put(flush());
    },
  },
  false
); // Don't re-auth if the auth error is throwing from logout api call or it will be infinite loop

/**
 * send meta beacon
 * @param  {Boolean} enabled
 */
function sendMetaBeacon(enabled: boolean, feature: string) {
  const baseConfig = {
    category: I13N_CATEGORY_APP,
    action: I13N_ACTION_CHANGE_VALUE,
    sampleRate: 0.01,
  };

  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    ...baseConfig,
    label: 'UpdateLogic',
    message: `${feature} - ${enabled ? 'enabled' : 'disabled'} by meta`,
  });
}

function* handleNotifications() {
  const notifications = yield* select(getProfileNotifications);
  if (!notifications?.length) {
    return;
  }

  yield* all(
    notifications.map((notification) => {
      const { type, meta } = notification;
      const tier = type === 'banned' ? 'banned' : meta?.tier;
      if (tier && (type === 'banned' || type === 'warning')) {
        return put(
          openModal(MODAL_WARNING, {
            props: { tier },
          })
        );
      }
      return null;
    })
  );
}

export function* _sendTermsUpdateAcceptedSaga() {
  const version = yield* select(getTermsVersion);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  yield* call(yield* call(getServiceFn, PROFILE_SERVICE, 'submitTermsOfService' as const), version);
  executeEvent(I13N_TYPE_VIEW, {
    category: I13N_CATEGORY_SETTINGS,
    action: I13N_TYPE_FEATURE_COUNT,
    label: 'UserLogic',
    message: 'Terms Update Modal',
  });
}
export const sendTermsUpdateAcceptedSaga = withErrorHandling(_sendTermsUpdateAcceptedSaga, {
  name: 'sendTermsUpdateAcceptedSaga',
});

export const verifyLoginRedirectSaga = withErrorHandling(
  function* _verifyLoginRedirectSaga() {
    yield* call(setHelpFormCookies);

    window.location.href = config.helpForm.url;
  },
  { name: 'verifyLoginRedirectSaga' }
);

export const requestAgeVerificationUrlSaga = withErrorHandling(
  function* _requestAgeVerificationUrlSaga() {
    const url = yield* select(getAgeVerificationUrl);
    if (!url) {
      const response = yield* call(
        yield* call(getServiceFn, USER_SERVICE, 'getAgeVerificationUrl' as const)
      );
      yield* put(requestAgeVerificationUrlSuccess(response.data));
    }
  },
  {
    name: 'requestAgeVerificationUrlSaga',
    *errorHandler(e: any) {
      yield* put(requestAgeVerificationUrlError(e?.error?.code));
    },
  }
);

export const verifyAgeVerificationStatusSaga = withErrorHandling(
  function* _verifyAgeVerificationStatusSaga() {
    const status = yield* select(getDeepLinkOtp);
    const isSuccess = status === 'success';

    yield* put(
      showNotification({
        theme: isSuccess ? NOTIFICATION_THEME_SUCCESS : NOTIFICATION_THEME_ERROR,
        title: [
          { id: isSuccess ? 'AgeVerification.bannerHeader' : 'AgeVerification.bannerErrorHeader' },
        ],
        body: [
          {
            id: isSuccess ? 'AgeVerification.bannerSubtext' : 'AgeVerification.bannerErrorSubtext',
          },
        ],
      })
    );
  },
  { name: 'verifyAgeVerificationStatusSaga' }
);
